import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const initialTables = [
  {
    id: '1',
    mesa: 5,
    cliente: 'João Silva',
    email: 'joao@email.com',
    telefone: '(11) 98765-4321',
    cpf: '123.456.789-00',
    pessoas: 4,
    chegada: '14:30',
    status: 'aberta',
    produtos: [],
    garcons: ['Carlos', 'Ana'],
    garconLogs: [
      {
        nome: 'Carlos',
        produto: 'Cerveja Skol',
        quantidade: 2,
        timestamp: '2025-10-30T18:05:00.000Z'
      },
      {
        nome: 'Ana',
        produto: 'Refrigerante',
        quantidade: 1,
        timestamp: '2025-10-30T18:12:30.000Z'
      }
    ]
  },
  {
    id: '2',
    mesa: 12,
    cliente: 'Maria Santos',
    email: 'maria@email.com',
    telefone: '(11) 99876-5432',
    cpf: '987.654.321-00',
    pessoas: 6,
    chegada: '13:00',
    saida: '15:00',
    status: 'fechada',
    produtos: [
      { nome: 'Cerveja Heineken', quantidade: 5, preco: 18.0 },
      { nome: 'Chopp', quantidade: 4, preco: 25.0 }
    ],
    garcons: ['Pedro', 'Carlos', 'Ana'],
    garconLogs: [
      {
        nome: 'Pedro',
        produto: 'Cerveja Heineken',
        quantidade: 3,
        timestamp: '2025-10-29T22:40:10.000Z'
      },
      {
        nome: 'Carlos',
        produto: 'Chopp',
        quantidade: 2,
        timestamp: '2025-10-29T22:52:45.000Z'
      }
    ]
  },
  {
    id: '3',
    mesa: 8,
    cliente: 'Pedro Costa',
    email: 'pedro@email.com',
    telefone: '(11) 91234-5678',
    cpf: '456.789.123-00',
    pessoas: 2,
    chegada: '15:30',
    status: 'aberta',
    produtos: [
      { nome: 'Refrigerante', quantidade: 1, preco: 8.0 },
      { nome: 'Cerveja Brahma', quantidade: 2, preco: 14.0 }
    ],
    garcons: ['Ana'],
    garconLogs: [
      {
        nome: 'Ana',
        produto: 'Cerveja Brahma',
        quantidade: 1,
        timestamp: '2025-10-30T19:03:20.000Z'
      }
    ]
  },
  {
    id: '4',
    mesa: 15,
    cliente: 'Ana Oliveira',
    email: 'ana@email.com',
    telefone: '(11) 99631-2109',
    cpf: '789.123.456-00',
    pessoas: 8,
    chegada: '12:50',
    saida: '13:45',
    status: 'fechada',
    produtos: [
      { nome: 'Cerveja Brahma', quantidade: 8, preco: 14.0 },
      { nome: 'Refrigerante', quantidade: 4, preco: 8.0 }
    ],
    garcons: ['Carlos', 'Pedro'],
    garconLogs: [
      {
        nome: 'Pedro',
        produto: 'Refrigerante',
        quantidade: 4,
        timestamp: '2025-10-29T17:15:00.000Z'
      }
    ]
  }
];

const STORAGE_KEY = 'cloe_tables_state_v1';

const TableContext = createContext();

export const useTableContext = () => {
  const ctx = useContext(TableContext);
  if (!ctx) {
    throw new Error('useTableContext must be used within a TableProvider');
  }
  return ctx;
};

const loadFromStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (error) {
    console.warn('Erro ao carregar dados locais das mesas:', error);
    return null;
  }
};

const persistToStorage = (tables) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tables));
  } catch (error) {
    console.warn('Erro ao salvar dados locais das mesas:', error);
  }
};

export const TableProvider = ({ children }) => {
  const [tables, setTables] = useState(() => loadFromStorage() ?? initialTables);

  useEffect(() => {
    persistToStorage(tables);
  }, [tables]);

  const value = useMemo(
    () => ({
      tables,
      setTables,
      addProductsToTable: (tableId, products) => {
        setTables((prev) => {
          const updated = prev.map((table) => {
            if (table.id !== tableId) return table;

            const mergedProducts = [...table.produtos];
            const existingLogs = table.garconLogs || [];
            const defaultGarcon = table.garcons?.[0] || 'Equipe';
            const newLogs = [];

            products.forEach((prod) => {
              const existingIdx = mergedProducts.findIndex((item) => item.nome === prod.nome);
              if (existingIdx >= 0) {
                mergedProducts[existingIdx] = {
                  ...mergedProducts[existingIdx],
                  quantidade: mergedProducts[existingIdx].quantidade + prod.quantidade
                };
              } else {
                mergedProducts.push(prod);
              }

              newLogs.push({
                nome: prod.garcon || defaultGarcon,
                produto: prod.nome,
                quantidade: prod.quantidade,
                timestamp: new Date().toISOString()
              });
            });

            const updatedTable = {
              ...table,
              produtos: mergedProducts,
              garconLogs: [...existingLogs, ...newLogs]
            };
            return updatedTable;
          });

          return updated;
        });
      },
      closeTable: (tableId) => {
        const now = new Date();
        const saida = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
        
        setTables((prev) =>
          prev.map((table) => {
            if (table.id !== tableId) return table;
            
            return {
              ...table,
              status: 'fechada',
              saida: saida
            };
          })
        );
      },
      transferTable: (sourceTableId, targetTableId) => {
        setTables((prev) => {
          const sourceTable = prev.find(t => t.id === sourceTableId);
          const targetTable = prev.find(t => t.id === targetTableId);
          
          if (!sourceTable || !targetTable) return prev;
          if (sourceTable.status !== 'aberta') return prev;
          
          return prev.map((table) => {
            if (table.id === targetTableId) {
              // Adiciona produtos da mesa origem na mesa destino
              const mergedProducts = [...table.produtos];
              sourceTable.produtos.forEach((prod) => {
                const existingIdx = mergedProducts.findIndex((item) => item.nome === prod.nome);
                if (existingIdx >= 0) {
                  mergedProducts[existingIdx] = {
                    ...mergedProducts[existingIdx],
                    quantidade: mergedProducts[existingIdx].quantidade + prod.quantidade
                  };
                } else {
                  mergedProducts.push(prod);
                }
              });
              
              return {
                ...table,
                produtos: mergedProducts
              };
            }
            
            if (table.id === sourceTableId) {
              // Limpa produtos da mesa origem
              return {
                ...table,
                produtos: []
              };
            }
            
            return table;
          });
        });
      },
      pauseTable: (tableId) => {
        setTables((prev) =>
          prev.map((table) => {
            if (table.id !== tableId) return table;
            
            return {
              ...table,
              status: 'pausada'
            };
          })
        );
      },
      resumeTable: (tableId) => {
        setTables((prev) =>
          prev.map((table) => {
            if (table.id !== tableId) return table;
            
            return {
              ...table,
              status: 'aberta'
            };
          })
        );
      },
      addPartialPayment: (tableId, amount) => {
        setTables((prev) =>
          prev.map((table) => {
            if (table.id !== tableId) return table;
            
            const existingPayments = table.pagamentosParciais || [];
            const now = new Date();
            const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
            
            return {
              ...table,
              pagamentosParciais: [
                ...existingPayments,
                {
                  amount: amount,
                  time: time,
                  date: now.toISOString()
                }
              ]
            };
          })
        );
      },
      createTable: (tableData) => {
        const newTable = {
          id: Date.now().toString(),
          mesa: tableData.mesa,
          cliente: tableData.cliente,
          email: tableData.email,
          telefone: tableData.telefone,
          cpf: tableData.cpf,
          pessoas: tableData.pessoas,
          chegada: tableData.chegada || new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
          status: 'aberta',
          produtos: [],
          garcons: tableData.garcons || [],
          garconLogs: []
        };
        
        setTables((prev) => [...prev, newTable]);
        return newTable;
      }
    }),
    [tables]
  );

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
};
